import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { isInternalRole } from '@shared/domain';
import { loginRequest, logoutRequest } from '../api/endpoints';
import { refreshAccessToken } from '../api/http';
import { session, type Claims } from './session';

type AuthStatus = 'restoring' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: AuthStatus;
  claims: Claims | null;
  isStaff: boolean;
  login: (tenant: string, email: string, password: string, remember: boolean) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_HINT = 'ro_has_session=1';
const hasSessionHint = (): boolean => document.cookie.split('; ').includes(SESSION_HINT);
const clearSessionHint = (): void => {
  document.cookie = 'ro_has_session=; Max-Age=0; Path=/; SameSite=Strict';
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const claims = useSyncExternalStore(session.subscribe, session.getClaims, session.getClaims);
  const [restoring, setRestoring] = useState(hasSessionHint);

  // Restore the session from the httpOnly refresh cookie on first load.
  useEffect(() => {
    if (!hasSessionHint()) return undefined; // anonymous visitor: skip the network round-trip
    let active = true;
    refreshAccessToken()
      .catch(clearSessionHint) // expired or revoked session → anonymous; not an error condition
      .finally(() => {
        if (active) setRestoring(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Wipe cached server state whenever the identity goes away, so the next user never sees stale data.
  useEffect(() => {
    if (!claims) queryClient.clear();
  }, [claims, queryClient]);

  // Proactively refresh one minute before expiry to avoid a 401 round-trip on the next request.
  useEffect(() => {
    if (!claims) return undefined;
    const delay = Math.max(claims.expiresAt - Date.now() - 60_000, 5_000);
    const timer = window.setTimeout(() => void refreshAccessToken().catch(() => undefined), delay);
    return () => window.clearTimeout(timer);
  }, [claims]);

  const login = useCallback(async (tenant: string, email: string, password: string, remember: boolean) => {
    const { accessToken } = await loginRequest({ tenant, email, password, remember });
    session.set(accessToken);
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      session.clear();
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: restoring ? 'restoring' : claims ? 'authenticated' : 'anonymous',
      claims,
      isStaff: claims ? isInternalRole(claims.role) : false,
      login,
      logout,
    }),
    [restoring, claims, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
